<template>
  <div class="vehicle-information" :class="statusClass">
    <section
      class="vehicle-information__summary"
      :class="[disabledClass, themeClass, paddingsClass]"
    >
      <figure v-if="canShowFigure" :class="hasNoSecondaryInformationClass">
        <img :src="vehicleInformationSorted.image.url" alt="Vehicle Image" />
        <figcaption
          class="vehicle-information__summary--title wb-type-heading-s"
        >
          <span
            class="vehicle-information__summary--title__text"
            :class="resultsPageClass"
            >{{ imageTitle }}</span
          >

          <button
            type="button"
            @click="toggleFiltersModal(true)"
            class="vehicle-information__summary__edit-btn wb-button wb-button--tertiary"
            v-if="canShowEditPreferencesBtn"
          >
            <wb-icon
              class="vehicle-information__summary__edit-btn__icon"
              :name="'bds/pencil/24'"
            />
            {{ $t('shared.editVehiclePreferences.button') }}
          </button>
        </figcaption>

        <div
          v-if="shouldShowVehicleInformation"
          class="vehicle-information__summary--motorization"
        >
          <div
            v-for="item in vehicleInformationSorted.primary"
            :key="item.value"
            class="vehicle-information__summary--motorization-item wb-type-copy-secondary"
            :class="itemClasses(item.class)"
          >
            <wb-icon
              class="vehicle-information__summary--motorization-item__icon"
              :name="item.icon"
            />
            {{ item.value }}
          </div>
        </div>
        <div
          v-else
          class="vehicle-information__summary--no-preferences-added wb-type-copy-secondary"
        >
          {{ $t('otbs.vehicleInformation.no.preferences') }}
        </div>
        <button
          type="button"
          @click="toggleFiltersModal(true)"
          class="wb-button wb-button--tertiary wb-button--large"
          v-if="canShowVehiclePreferencesBtn"
        >
          <wb-icon
            class="vehicle-information__summary__edit-btn__icon"
            :name="'bds/pencil/24'"
          />
          {{ $t('shared.vehiclePreferences.button') }}
        </button>
      </figure>
    </section>
    <summary-card
      v-if="canShowSecondaryInformation"
      type="details"
      :disabled="disabled"
      :theme="theme"
      :paddings="paddings"
      :class="themeClass"
      :summaryInformation="vehicleInformationSorted.secondary"
      :status="status"
      :path="path"
    />
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from 'vue';

import { SummaryCard } from '../index';

import { useDirection } from '../../composables/direction.composable';

import { Maybe } from '../../graphql/schema';

import GenericReceivedObject from '../../models/utilities/generic-received-object.model';
import { VehicleInformation } from '../../models/entities/vehicle-information.model';
import { TestDriveStatus } from '../../models/utilities/testdrive-status.enum';
import UtilsService from '../../services/utils.service';
import { useStore } from '@dh-io-otb/shared/src/composables/store.composable';
import { useMediaQuery } from '@dh-io-otb/shared/src/composables/media-query.composable';
import { TestDriveMode } from '../../models/utilities/testdrive-mode.enum';
import { ROUTER } from '../../../../otbc/src/models/constants';

export default defineComponent({
  name: 'otb-vehicle-information',
  components: {
    SummaryCard,
  },
  props: {
    disabled: Boolean,
    vehicleInformation: {
      type: Object as () => VehicleInformation,
      default: null,
    },
    vansInformation: {
      type: Object as () => VehicleInformation,
      default: null,
    },
    status: {
      type: String as () => TestDriveStatus,
      default: TestDriveStatus.BOOKED,
    },
    theme: {
      type: String,
    },
    paddings: {
      type: String,
    },
    path: {
      type: String,
    },
  },
  setup(props) {
    const { isRTL } = useDirection();
    const { state, commit } = useStore();
    const { isMobile } = useMediaQuery();
    const showFiltersModal = ref(false);

    const toggleFiltersModal = (state?: boolean): void => {
      showFiltersModal.value = state ?? !showFiltersModal.value;
      commit('showFiltersModal', showFiltersModal.value);
    };

    const bookingActive = computed((): boolean => {
      return state.configuration.bookingActive;
    });
    const isRequestPathWithoutConfirmedMotorisation = computed((): boolean => {
      return (
        props.path === ROUTER.REQUEST && !state.otbcFlow.confirmedMotorisation
      );
    });

    const canShowVehiclePreferencesBtn = computed((): boolean => {
      return (
        isRequestPathWithoutConfirmedMotorisation.value &&
        !state.otbcFlow.isVehicleFilterConfirmed
      );
    });

    const canShowEditPreferencesBtn = computed((): boolean => {
      return (
        (canShowSecondaryInformation.value ||
          vehicleInformationSorted.value?.primary.length ||
          vehicleInformationSorted.value?.secondary.length) &&
        props.path === ROUTER.REQUEST &&
        state.otbcFlow.isVehicleFilterConfirmed
      );
    });

    const canShowFigure = computed((): boolean => {
      return !!(
        props.vehicleInformation &&
        (props.vehicleInformation as VehicleInformation).image
      );
    });

    const shouldShowVehicleInformation = computed((): boolean => {
      const hasVehicleInformation =
        vehicleInformationSorted.value.primary.length ||
        vehicleInformationSorted.value.secondary.length;
      return props.path === ROUTER.REQUEST
        ? hasVehicleInformation && state.otbcFlow.isVehicleFilterConfirmed
        : hasVehicleInformation;
    });

    const canShowSecondaryInformation = computed((): boolean => {
      return !!(
        props.vehicleInformation &&
        (props.vehicleInformation as VehicleInformation).secondary &&
        (props.vehicleInformation as VehicleInformation).secondary.length &&
        !isRequestPathWithoutConfirmedMotorisation.value
      );
    });

    const disabledClass = computed((): string => {
      return props.disabled ? 'vehicle-information--disabled' : '';
    });

    const themeClass = computed((): string => {
      return props.theme === 'white' ? 'white-theme' : '';
    });

    const paddingsClass = computed((): string => {
      return props.paddings === 'none' ? 'no-paddings' : 'default-paddings';
    });

    const vehicleInformationSorted = computed((): Maybe<VehicleInformation> => {
      const info = props.vehicleInformation;

      if (info) {
        info.primary.sort(
          (a: GenericReceivedObject, b: GenericReceivedObject) =>
            a.order - b.order
        );
        info.secondary.sort(
          (a: GenericReceivedObject, b: GenericReceivedObject) =>
            a.order - b.order
        );
      }
      return info;
    });

    const statusClass = computed((): string => {
      return UtilsService.getStatusClass(props.status);
    });

    const resultsPageClass = computed((): string => {
      return props.path === ROUTER.SCHEDULE
        ? 'vehicle-information__summary--title--results-page'
        : '';
    });

    const hasNoSecondaryInformationClass = computed((): string => {
      return !canShowSecondaryInformation.value
        ? 'vehicle-information__summary--has-no-secondary-information'
        : '';
    });

    const motorizationItemRTL = computed((): string => {
      return isRTL.value
        ? 'vehicle-information__summary--motorization-item-rtl'
        : '';
    });

    const itemClasses = (className: string): string => {
      return `${className} ${motorizationItemRTL.value}`;
    };

    const imageTitle = computed((): string => {
      if (!state.configuration.bookingActive) {
        return (
          state.otbcFlow.confirmedMotorisation.name || state.otbcFlow.model.name
        );
      } else {
        return (
          vehicleInformationSorted.value.image.title ||
          state.otbcFlow.model.name
        );
      }
    });

    const shouldShowSummaryTitle = computed(() =>
      state.otbcFlow.testDriveMode === TestDriveMode.REQUEST
        ? !!state.otbcFlow.selectedMotorisation
        : state.otbcFlow.testDriveMode === TestDriveMode.BOOKING
    );
    return {
      isRTL,
      canShowFigure,
      canShowSecondaryInformation,
      disabledClass,
      themeClass,
      vehicleInformationSorted,
      statusClass,
      hasNoSecondaryInformationClass,
      motorizationItemRTL,
      itemClasses,
      paddingsClass,
      imageTitle,
      isMobile,
      shouldShowSummaryTitle,
      shouldShowVehicleInformation,
      toggleFiltersModal,
      showFiltersModal,
      bookingActive,
      canShowVehiclePreferencesBtn,
      canShowEditPreferencesBtn,
      resultsPageClass,
      isRequestPathWithoutConfirmedMotorisation,
    };
  },
});
</script>

<style scoped src="./vehicle-information.scss" lang="scss"></style>
